import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $insertTableColumnAtNode, $insertTableRowAtNode, $isTableCellNode, $isTableNode, $isTableRowNode } from '@lexical/table';
import type { NodeKey } from 'lexical';
import { Plus } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePointerTracking } from '../../hooks/usePointerTracking';
import { EditorPortal } from '../../ui/EditorPortal';
import { $getNodeOfType } from '../../utils/nodes';
import { $getCellFromDOM } from './tableSelection';

const BAR_LENGTH = 40;
const BAR_THICKNESS = 14;

type Side = 'top' | 'bottom' | 'left' | 'right';

type Hover = { tableKey: NodeKey; rect: DOMRect; sides: Side[] };

/** Within the table plus the strip around it where the bars sit. */
const isNear = (rect: DOMRect, x: number, y: number) => {
  const margin = BAR_THICKNESS;
  return x >= rect.left - margin && x <= rect.right + margin && y >= rect.top - margin && y <= rect.bottom + margin;
};

/** Each bar straddles the middle of its edge, so it stays inside the editor's padding. */
const barStyle = (side: Side, rect: DOMRect): CSSProperties => {
  const horizontal = side === 'top' || side === 'bottom';
  return {
    width: horizontal ? BAR_LENGTH : BAR_THICKNESS,
    height: horizontal ? BAR_THICKNESS : BAR_LENGTH,
    left: horizontal ? rect.left + rect.width / 2 - BAR_LENGTH / 2 : (side === 'left' ? rect.left : rect.right) - BAR_THICKNESS / 2,
    top: horizontal ? (side === 'top' ? rect.top : rect.bottom) - BAR_THICKNESS / 2 : rect.top + rect.height / 2 - BAR_LENGTH / 2,
  };
};

/**
 * "+" buttons on a table's edges, shown while hovering its outer rows or columns.
 * - Top/bottom add a row before the first / after the last; left/right do the same for columns.
 * - Hover is tracked on the document so moving from a cell onto a button keeps it visible.
 */
export const TableHoverActionsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const editable = useLexicalEditable();
  const { t } = useTranslation('common');
  const [hover, setHover] = useState<Hover | null>(null);

  usePointerTracking(
    {
      onMove: (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('[data-table-hover-action]')) return;

        const cellElement = target?.closest('td, th');
        if (!cellElement || !editor.getRootElement()?.contains(cellElement)) {
          // Crossing the gap between table and button must not hide the button
          setHover((prev) => (prev && isNear(prev.rect, event.clientX, event.clientY) ? prev : null));
          return;
        }

        setHover(
          editor.read(() => {
            const found = $getCellFromDOM(cellElement);
            const tableElement = cellElement.closest('table');
            if (!found || !tableElement) return null;
            const { table, rect } = found;

            const sides: Side[] = [];
            if (rect.rowIndex === 0) sides.push('top');
            if (rect.rowIndex + rect.rowSpan >= table.getChildrenSize()) sides.push('bottom');
            if (rect.columnIndex === 0) sides.push('left');
            if (rect.columnIndex + rect.colSpan >= table.getColumnCount()) sides.push('right');
            return sides.length > 0 ? { tableKey: table.getKey(), rect: tableElement.getBoundingClientRect(), sides } : null;
          }),
        );
      },
      // Positions are viewport-fixed, so any scroll makes them stale
      onScroll: () => setHover(null),
    },
    editable,
  );

  if (!hover || !editable) return null;

  const insert = (side: Side) => {
    editor.update(() => {
      const table = $getNodeOfType(hover.tableKey, $isTableNode);
      // Bottom anchors on the last row; every other side on the first row
      const row = side === 'bottom' ? table?.getLastChild() : table?.getFirstChild();
      if (!$isTableRowNode(row)) return;
      const cell = side === 'right' ? row.getLastChild() : row.getFirstChild();
      if (!$isTableCellNode(cell)) return;

      if (side === 'top' || side === 'bottom') $insertTableRowAtNode(cell, side === 'bottom');
      else $insertTableColumnAtNode(cell, side === 'right', false);
    });
    setHover(null);
  };

  const label = (side: Side) => t(side === 'top' || side === 'bottom' ? 'editor.table.add-row' : 'editor.table.add-column');

  return (
    <EditorPortal className="contents">
      {hover.sides.map((side) => (
        <button
          key={side}
          type="button"
          data-table-hover-action
          title={label(side)}
          aria-label={label(side)}
          className="border-greyscale-15 hover:bg-accent-1-100 hover:border-accent-1-100 text-text-2 z-popover fixed flex cursor-pointer items-center justify-center rounded-full border bg-white shadow-sm transition-colors hover:text-white"
          style={barStyle(side, hover.rect)}
          onClick={() => insert(side)}
        >
          <Plus className="size-3" strokeWidth={2.5} aria-hidden />
        </button>
      ))}
    </EditorPortal>
  );
};
